'use client';

import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import { Button } from '@/components';
import { MONO_FONT } from '@/theme';
import { analytics } from '@/utils/analytics';
import { GTM_EVENTS, GTM_SOURCES } from '@/constants/gtm';
import { WEB3FORMS_KEY } from '@/libs/leads';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// `source` (GTM) y `origin` (campo "Origen" del lead en Web3Forms) identifican desde qué
// sección se suscribió la persona; por defecto, el blog.
export default function NewsletterStrip({
  source = GTM_SOURCES.BLOG_NEWSLETTER,
  origin = 'Blog · Newsletter',
}) {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [botcheck, setBotcheck] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (botcheck) return; // honeypot
    if (!EMAIL_RE.test(email.trim())) {
      setError('Ingresa un email válido.');
      return;
    }
    if (!consent) {
      setError('Necesitamos tu consentimiento para suscribirte.');
      return;
    }
    setError('');
    setStatus('loading');

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: 'Nueva suscripción al newsletter — Bison Agents',
          from_name: 'Newsletter Bison Agents',
          Email: email.trim(),
          Origen: origin,
        }),
      });
      const data = await res.json();
      if (data.success) {
        analytics.custom(GTM_EVENTS.NEWSLETTER_SIGNUP, { source });
        setStatus('success');
        setEmail('');
        setConsent(false);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <Box
      id="newsletter"
      sx={{
        p: { xs: '28px 24px', md: '40px' },
        bgcolor: 'grey.50',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 1,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' },
        gap: { xs: 3, md: 4 },
        alignItems: 'center',
      }}
    >
      <Box>
        <Box
          component="span"
          sx={{
            display: 'block',
            fontFamily: MONO_FONT,
            fontSize: '11px',
            fontWeight: 500,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'text.accent',
            mb: 2,
          }}
        >
          Newsletter quincenal
        </Box>
        <Typography
          component="h3"
          sx={{
            fontSize: 'clamp(18px, 2vw, 22px)',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: 'text.primary',
            mb: 1,
          }}
        >
          Posts largos, cero relleno, dos veces por semana.
        </Typography>
        <Typography sx={{ fontSize: '14.5px', lineHeight: 1.6, color: 'grey.600', maxWidth: '50ch' }}>
          Sin tips de cinco minutos, sin venta. Los mismos posts del blog, en tu inbox, antes de
          que sean indexados.
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {/* Honeypot anti-spam */}
        <Box
          component="input"
          type="text"
          name="botcheck"
          value={botcheck}
          onChange={(e) => setBotcheck(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          sx={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
        />

        {status === 'success' ? (
          <Box
            role="status"
            sx={{
              px: '20px',
              py: '14px',
              bgcolor: 'primary.lightest',
              border: '1px solid',
              borderColor: 'primary.light',
              borderRadius: '4px',
              color: 'primary.darker',
              fontSize: '14px',
              lineHeight: 1.5,
            }}
          >
            Listo, quedaste suscrito/a. El próximo envío te llega a tu inbox.
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
              <Box
                component="input"
                type="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError('');
                }}
                placeholder="tu@empresa.com"
                aria-label="Email para newsletter"
                aria-invalid={!!error}
                required
                sx={{
                  flex: 1,
                  minWidth: 0,
                  px: '14px',
                  py: '11px',
                  border: '1px solid',
                  borderColor: error ? 'error.main' : 'grey.300',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                  fontSize: '15px',
                  color: 'text.primary',
                  bgcolor: 'common.white',
                  outline: 'none',
                  '&:focus': {
                    borderColor: 'primary.main',
                    boxShadow: '0 0 0 3px rgba(0,159,139,0.12)',
                  },
                }}
              />
              <Button type="submit" variant="contained" disabled={status === 'loading'}>
                {status === 'loading' ? 'Enviando…' : 'Suscribir'}
              </Button>
            </Box>

            {/* Consentimiento (Ley 21.719) */}
            <Box component="label" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1.5, cursor: 'pointer' }}>
              <Box
                component="input"
                type="checkbox"
                checked={consent}
                onChange={(e) => {
                  setConsent(e.target.checked);
                  if (error) setError('');
                }}
                sx={{ accentColor: '#009F8B', cursor: 'pointer', mt: '2px', flexShrink: 0 }}
              />
              <Typography component="span" sx={{ fontSize: '12px', lineHeight: 1.5, color: 'grey.600' }}>
                Acepto recibir el newsletter y la{' '}
                <Box component={Link} href="/privacidad" sx={{ color: 'text.accent', textDecoration: 'underline' }}>
                  política de privacidad
                </Box>
                .
              </Typography>
            </Box>

            {(error || status === 'error') && (
              <Typography
                role="alert"
                sx={{ fontSize: '12.5px', lineHeight: 1.5, color: 'error.main', mt: 1 }}
              >
                {error || 'No pudimos completar la suscripción. Reintenta en un momento.'}
              </Typography>
            )}

            <Typography
              sx={{
                fontFamily: MONO_FONT,
                fontSize: '11px',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'text.muted',
                mt: 1.5,
              }}
            >
              Sin spam · Baja en un clic
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}
